import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, GetState, RootState } from '@SonarCX/store'

import { getUserFavorites, updateUserFavorites } from '@SonarCX/lib/api'
import { AddErrorAlert, AddSuccessAlert } from '@SonarCX/slices/alert'

type FavoritesState = {
  favorites: APIFavorite[]
  loaded: boolean
}

const initialState: FavoritesState = {
  favorites: [],
  loaded: false
}

const favoritesSlice = createSlice({
  name: 'favorites',
  initialState,
  reducers: {
    load: (state: FavoritesState, action: PayloadAction<APIFavorite[]>) => {
      state.favorites = action.payload
      state.loaded = true
    },

    add: (state: FavoritesState, action: PayloadAction<APIFavorite>) => {
      state.favorites.push(action.payload)
    },

    remove: (state: FavoritesState, action: PayloadAction<string>) => {
      const id = action.payload

      state.favorites = state.favorites.filter(
        favorite => favorite.item_id !== id
      )
    }
  }
})

const { load, add, remove } = favoritesSlice.actions

export const fetchFavorites = (userID: string) => {
  return async (dispatch: AppDispatch) => {
    try {
      const favorites = await getUserFavorites(userID)

      dispatch(load(favorites))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const addFavorite = (userID: string, newFavorite: APIFavorite) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { favorites } = selectFavorites(getState())

      await updateUserFavorites(userID, [...favorites, newFavorite])

      dispatch(add(newFavorite))
      dispatch(AddSuccessAlert('Favorite saved'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const removeFavorite = (userID: string, id: string) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { favorites } = selectFavorites(getState())

      const newFavorites = favorites.map(favorite => {
        if (favorite.item_id === id) {
          return { ...favorite, deleted: true }
        }

        return favorite
      })
      await updateUserFavorites(userID, newFavorites)

      dispatch(remove(id))
      dispatch(AddSuccessAlert('Favorite removed'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const patchFavorites = (userID: string, newFavorites: APIFavorite[]) => {
  return async (dispatch: AppDispatch, getState: GetState) => {
    try {
      const { favorites } = selectFavorites(getState())

      // Merge existing and new favorites
      const existing: Record<string, APIFavorite> = {}

      // Add existing favorites to the object
      favorites.forEach(favorite => {
        existing[favorite.item_id] = favorite
      })

      // Overwrite existing favorites with new favorites
      newFavorites.forEach(favorite => {
        existing[favorite.item_id] = favorite
      })

      // Store combined favorites
      const merged = Object.values(existing)
      await updateUserFavorites(userID, merged)
      dispatch(load(merged.filter(favorite => !favorite.deleted)))

      dispatch(AddSuccessAlert('Favorites updated'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const updateFavoritesOrder = (
  userID: string,
  newFavorites: APIFavorite[]
) => {
  return async (dispatch: AppDispatch) => {
    try {
      // clean up favorites first
      const cleanUp = newFavorites.map(favorite => ({
        ...favorite,
        deleted: true
      }))
      await updateUserFavorites(userID, cleanUp)
      // then add them back in the new order
      await updateUserFavorites(userID, newFavorites)
      dispatch(load(newFavorites.filter(favorite => !favorite.deleted)))
      dispatch(AddSuccessAlert('Favorites updated'))
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
    }
  }
}

export const selectFavorites = (state: RootState) => state.favorites

export default favoritesSlice.reducer
